// Field definitions for the shared gazetteer (TaskPogoPlace), used by the
// /me/places editor. Same shape as TripFields.mjs: a flat field list plus the
// validator useAutosave holds saves on.
//
// Two keys are deliberately handled outside this list:
// - `nameKey` IS here, but it is derived, never typed. The editor recomputes it
//   from `name` (normalizePlaceName) on every keystroke and renders the row
//   read-only; it lives in the list so buildEditedFields carries it into the
//   payload like any other field.
// - `source` is absent. It records HOW a row got its position ('geocode' |
//   'user') and is display-only -- editing it would let someone relabel a
//   geocoder guess as a hand correction, which is the one thing the flag
//   exists to distinguish.

import { trimmedStringOrNull } from './Common.mjs';
import { haversineNm } from './GeoBounds.mjs';

// Single source of truth for name matching, used by lookup AND writeback so the
// two can never disagree about which row a name refers to.
//
// It lives in fleet-shared rather than beside its callers in
// src/utilities/places.mjs because that module imports Database.mjs (and so
// aws-amplify), which Node scripts cannot load -- and scripts/backfill-places.mjs
// has to derive nameKey exactly the way the app does or it writes rows the
// app's placesByNameKey lookups can never find.
export function normalizePlaceName(name) {
  return (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

// Everything before the first comma: "Roche Harbor, San Juan" -> "Roche Harbor".
//
// Geocoder picks and hand-typed names both arrive qualified, because the
// qualifier is what makes the name unambiguous to MATCH on. It is exactly the
// part you don't need once the point is drawn on a chart you're already
// looking at.
//
// A name that is all qualifier -- one that starts with a comma -- keeps its
// full text rather than collapsing to an empty label. Same for a blank name:
// callers up the chain (waypointLabel) have their own fallbacks and need a
// falsy answer to reach them.
export function shortPlaceName(name) {
  const text = (name || '').trim();
  const comma = text.indexOf(',');
  if (comma < 0) return text;
  return text.slice(0, comma).trim() || text;
}

// The SHORT name for this row: the hand-curated `friendlyName` when there is
// one, else the comma rule. The column exists for the cases the rule gets
// wrong -- "Port of Friday Harbor" has no comma to cut at.
//
// Where it is used, in one sentence: a surface with a single line of room --
// a map marker, a table cell, a summary row -- shows this; a surface with room
// to be exact -- a popup, an edit box, a detail page, a destructive dialog --
// shows the full `name`.
//
// Display-only, and deliberately not part of any lookup: `nameKey` is derived
// from `name` and only from `name`, so renaming the label can never move a row
// between name keys or strand a waypoint's binding.
export function placeDisplayName(place) {
  return trimmedStringOrNull(place?.friendlyName) || shortPlaceName(place?.name);
}

// The same answer for a place name that is only a STRING -- a trip's `from`
// column, a logbook entry's `fields.to` -- rather than a row.
//
// A waypoint binds to its row by `placeId` and carries the label along as
// `mapLabel`. A From/To column has no id, so the name IS the binding: this
// looks it up the way resolveWaypoint's rule 3 bootstraps a legacy route,
// through the same `normalizePlaceName`, against the same `byKey` (already
// pickPlace-resolved by indexPlaces, so duplicates are decided in one place).
//
// A name with no row is returned UNCHANGED -- the shortening is a fact about a
// gazetteer row, not about punctuation, so a hand-typed "Ganges, Salt Spring"
// reads exactly as typed. So is anything that isn't a non-empty string, which
// keeps each caller's own null-to-'' handling working whatever order it runs in.
//
// A missing or unusable index is also "leave it alone", the policy
// resolveRouteAgainstPlaces holds: a failed loadPlaceIndex and a genuinely
// empty table are indistinguishable, and blanking a screenful of names on a
// network blip is far worse than showing them long for a moment.
//
// Caveat worth knowing at the call sites: a list cannot disambiguate the way a
// map can, so "Friday Harbor, WA" and "Friday Harbor, ON" both read "Friday
// Harbor" in adjacent rows. `friendlyName` is the escape hatch.
export function resolvePlaceName(name, placeIndex) {
  if (typeof name !== 'string') return name;
  const text = name.trim();
  if (!text) return name;

  const byKey = placeIndex?.byKey;
  if (!(byKey instanceof Map) || byKey.size === 0) return name;

  const row = byKey.get(normalizePlaceName(text));
  return row ? placeDisplayName(row) : name;
}

// What a read-only table cell needs: the text to draw, and the full name to
// hang off `title` -- `undefined` when they are the same, so a cell that wasn't
// shortened grows no redundant tooltip.
//
// One helper rather than the same two-line compare in three tables, because the
// "only when they differ" half is the part that is easy to get subtly wrong.
//
// `title` is a convenience, not the accessible path: it has no keyboard or
// touch affordance and screen readers treat it inconsistently. The record's own
// detail page is where the full name is always readable -- don't "fix" the
// hover by shortening that too.
export function placeCell(name, placeIndex) {
  const text = resolvePlaceName(name, placeIndex);
  return { text, title: text === name ? undefined : name };
}

// How close a new place has to be to an existing one before adding it is
// probably a mistake rather than a decision.
//
// Half a nautical mile is "the same harbor, near enough the same dock". It is
// deliberately loose: the check never blocks, it asks -- so a false positive
// costs one click, while a false negative costs a duplicate row that only
// scripts/merge-places.mjs can undo. Tighter than this and the case it exists
// for gets through: a geocoder's pin for a marina routinely lands a few hundred
// metres off the actual dock, which is how prod ended up with two rows for
// Victoria's Causeway Marina.
export const NEAR_PLACE_NM = 0.5;

// The gazetteer rows within `maxNm` of a point, nearest first, each carrying
// the distance that got it into the list.
//
// Pure -- no Amplify, no I/O, no caches -- so a script can ask the same
// question the UI asks. Distance is haversineNm, the same helper the weather
// email's station search uses; there is no second distance function in this
// codebase and there must not be.
//
// Two exclusions, and they are not the same kind of thing:
// - `excludeId` is "don't offer me myself", for a caller editing a row.
// - `excludeNameKey` is the important one. An EXACT name match is already a
//   no-op at every writer (recordPickedPlace returns early on findPlace, and
//   the editor warns through findDuplicatePlaces), so asking about one would
//   interrupt a gesture that was never going to create anything. This check is
//   for the row a name-only guard cannot see: same place, different spelling.
//
// A row with no usable position is skipped rather than treated as being at
// 0,0 -- validatePlace makes that state unreachable through the editor, but an
// imported or hand-written row can still carry a null, and a phantom match in
// the Gulf of Guinea would be worse than no match at all.
export function nearbyPlaces(point, places, { maxNm = NEAR_PLACE_NM, excludeId, excludeNameKey } = {}) {
  const lat = Number(point?.lat);
  const lon = Number(point?.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return [];

  const skipKey = excludeNameKey ? normalizePlaceName(excludeNameKey) : null;

  return (Array.isArray(places) ? places : [])
    .reduce((found, place) => {
      if (!place) return found;
      if (excludeId && place.id === excludeId) return found;
      if (skipKey && normalizePlaceName(place.nameKey ?? place.name) === skipKey) return found;

      const placeLat = Number(place.lat);
      const placeLon = Number(place.lon);
      if (!Number.isFinite(placeLat) || !Number.isFinite(placeLon)) return found;

      const distanceNm = haversineNm({ lat, lon }, { lat: placeLat, lon: placeLon });
      if (!Number.isFinite(distanceNm) || distanceNm > maxNm) return found;

      found.push({ ...place, distanceNm });
      return found;
    }, [])
    .sort((a, b) => a.distanceNm - b.distanceNm);
}

// How far off the existing place is, for a dialog whose whole job is letting
// someone tell "the same marina" from "the one next door".
//
// Tenths, not the whole miles TripRoute's formatAwayNm prints: that one
// floors at "<1 nm", which over a half-mile radius would render EVERY match
// identically and leave the number saying nothing at all.
export function formatNearbyNm(nm) {
  if (!Number.isFinite(nm)) return '';
  return `${nm.toFixed(1)} nm`;
}

// The facility booleans, as one group. Exported separately because two callers
// want exactly this list and nothing else around it: the edit page renders them
// as a single compact row of checkboxes, and the map popup renders the true
// ones as badges.
//
// All optional, and a null means "nobody has said" rather than "no" -- every
// row predating this feature has a null in every one of them. Nothing keys off
// the difference today; if something ever does, it must not read a blank as a
// denial. That matters most for the two negative flags: `noLanding` and
// `difficultLanding` are claims made only when true, and a blank one is still
// "nobody has said" -- never "the landing is fine".
//
// Existing keys stay first and in their original order: the popup renders
// badges in this order, and the checkbox row's positions are muscle memory.
//
// `symbol` is the one-glyph rendering of the facility, for a surface with a
// column per facility rather than a badge per facility (the /me/places table).
// Exactly one of `icon` (a Font Awesome 6 free solid name, without the `fa-`)
// or `letter`. It lives HERE rather than in a lookup table beside that table
// for the reason the registry exists at all: `name` is already a display fact
// sitting beside the key, and a second list keyed by facility would go stale
// the day another facility is added.
//
// Display-only. Nothing keys off it -- activeFacilities and the popup badges
// read `name`, and no writer, exporter or script sees it.
//
// Five of these are substitutes, not first choices: Font Awesome 6 free has no
// policeman's hat, no octagon stop sign, no rowboat, no second anchor and no
// shingle. `passport` is border control, `ban` is the universal no, and
// `sailboat` is the smallest boat available -- deliberately a different
// silhouette from Ferry's big `ship`. Day Anchor takes `sun` for the daylight
// half of "lunch hook, not overnight", because the one anchor glyph belongs to
// Anchorage; Gravel Beach falls back to a letter, because the only beach glyph
// is Sandy Beach's umbrella and drawing it twice would say the opposite of what
// the two columns are for.
//
// The CDN pin is FA **6.7.2** (`public/index.html`). Check that a new icon
// exists in the PINNED version's stylesheet, not just on the FA site -- a name
// the pinned build doesn't have renders as a blank cell in every row including
// the header, which is exactly what the letter fallback in `PlacesTable`
// exists to make look unfinished rather than empty.
// (`curl -s .../font-awesome/6.7.2/css/all.min.css | grep -c fa-<name>`.)
export const FACILITY_FIELDS = [
  { name: 'Anchorage', key: 'anchorage', type: 'boolean', group: 'facilities', symbol: { icon: 'anchor' } },
  { name: 'Marina', key: 'marina', type: 'boolean', group: 'facilities', symbol: { letter: 'M' } },
  { name: 'Fuel', key: 'fuel', type: 'boolean', group: 'facilities', symbol: { icon: 'gas-pump' } },
  { name: 'Shopping', key: 'shopping', type: 'boolean', group: 'facilities', symbol: { icon: 'cart-shopping' } },
  { name: 'Customs', key: 'customs', type: 'boolean', group: 'facilities', symbol: { icon: 'passport' } },
  { name: 'NEXUS', key: 'nexus', type: 'boolean', group: 'facilities', symbol: { letter: 'N' } },
  { name: 'Dinghy Dock', key: 'dinghyDock', type: 'boolean', group: 'facilities', symbol: { icon: 'sailboat' } },
  { name: 'Sandy Beach', key: 'sandyBeach', type: 'boolean', group: 'facilities', symbol: { icon: 'umbrella-beach' } },
  { name: 'Difficult Landing', key: 'difficultLanding', type: 'boolean', group: 'facilities', symbol: { icon: 'triangle-exclamation' } },
  { name: 'No Landing', key: 'noLanding', type: 'boolean', group: 'facilities', symbol: { icon: 'ban' } },
  { name: 'Near Airport', key: 'nearAirport', type: 'boolean', group: 'facilities', symbol: { icon: 'plane' } },
  { name: 'Ferry', key: 'ferry', type: 'boolean', group: 'facilities', symbol: { icon: 'ship' } },
  { name: 'Hiking', key: 'hiking', type: 'boolean', group: 'facilities', symbol: { icon: 'person-hiking' } },
  { name: 'Day Anchor', key: 'dayAnchor', type: 'boolean', group: 'facilities', symbol: { icon: 'sun' } },
  { name: 'Restaurant', key: 'restaurant', type: 'boolean', group: 'facilities', symbol: { icon: 'utensils' } },
  { name: 'Gravel Beach', key: 'gravelBeach', type: 'boolean', group: 'facilities', symbol: { letter: 'G' } },
  // Airport says the row IS one; Near Airport (above, which owns `plane`) says
  // a harbour has one close by -- different claims, and a field can carry both.
  // Airport is also the flag the trip maps draw a glyph for (PlacesLayer).
  { name: 'Airport', key: 'airport', type: 'boolean', group: 'facilities', symbol: { icon: 'plane-departure' } },
];

export const placeFields = [
  { name: 'Name', key: 'name', type: 'text' },
  {
    name: 'Friendly Name',
    key: 'friendlyName',
    type: 'text',
    note: 'Optional short label for map markers. Blank shows the name up to the first comma.',
  },
  {
    name: 'Name Key',
    key: 'nameKey',
    type: 'text',
    derived: true,
    note: 'Derived from Name (trimmed, lowercased, spaces collapsed). This is what lookups match.',
  },
  { name: 'Latitude', key: 'lat', type: 'number', step: 'any' },
  { name: 'Longitude', key: 'lon', type: 'number', step: 'any' },
  { name: 'Curated', key: 'curated', type: 'boolean' },
  ...FACILITY_FIELDS,
  { name: 'Notes', key: 'notes', type: 'textarea', rows: 4 },
];

// The subset the edit page renders as ordinary Field rows. The facilities get
// one hand-built row instead -- a standalone boolean Field would render its
// name twice (the row's <label> plus the Form.Check's own label).
export const placeRowFields = placeFields.filter((field) => field.group !== 'facilities');

// useAutosave's `validate`. All three columns are .required() on the model, and
// the number input hands back whatever parseFloat made of the keystrokes
// (EditForm's `min` only moves the spinner floor -- typing reaches the payload
// unchecked), so an out-of-range coordinate would otherwise be a hard AppSync
// error with no field context. Holding the save with an inline message instead.
//
// Unlike validateTripLeg, empty is NOT valid here: a place with no position is
// not a place, and there is no sensible default to fall back to.
export function validatePlace(state) {
  const fieldErrors = {};

  if (trimmedStringOrNull(state?.name) == null) {
    fieldErrors.name = 'Name is required.';
  }

  const checkCoordinate = (key, limit, label) => {
    const raw = state?.[key];
    if (raw === null || raw === undefined || raw === '') {
      fieldErrors[key] = `${label} is required.`;
      return;
    }
    const value = Number(raw);
    if (!Number.isFinite(value) || Math.abs(value) > limit) {
      fieldErrors[key] = `${label} must be between -${limit} and ${limit}.`;
    }
  };
  checkCoordinate('lat', 90, 'Latitude');
  checkCoordinate('lon', 180, 'Longitude');

  return Object.keys(fieldErrors).length > 0 ? { ok: false, fieldErrors } : { ok: true };
}

// Which row wins when a name has several (no unique constraint exists on a
// DynamoDB GSI, so a concurrent-create race can leave duplicates): curated
// ground truth first, else the oldest row -- deterministic on every client.
//
// Exported because indexPlaces is not the only reader that has to break a tie
// the same way: the typeahead ranks one row per nameKey and locatePlaceName
// resolves a single key, both in src/utilities/places.mjs.
export function pickPlace(items) {
  if (!items || items.length === 0) return null;
  return (
    items.find((place) => place.curated) ||
    [...items].sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''))[0]
  );
}

// A list of rows -> the { byId, byKey } index every gazetteer reader takes:
// resolveRouteAgainstPlaces, resolvePlaceName, placeCell, PlaceUsage.
//
// One builder so every caller agrees about duplicates -- all of them resolve a
// name through pickPlace, which is what keeps a curated row outranking its
// geocoded namesake no matter who asked.
//
// It lives HERE, in fleet-shared, rather than beside loadPlaceIndex in
// src/utilities/places.mjs, because that module imports Database.mjs and so
// aws-amplify, and a Lambda cannot load it -- the same constraint PlaceIO.mjs
// documents for isPlaceCandidate. The trip weather mail function builds its own
// index from rows it fetched itself, and a second copy of this rule would be
// free to disagree with the browser's about which duplicate wins.
export function indexPlaces(places) {
  const byId = new Map();
  const grouped = new Map();
  for (const place of places || []) {
    if (!place?.id) continue;
    byId.set(place.id, place);
    const key = place.nameKey || normalizePlaceName(place.name);
    if (!key) continue;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(place);
  }

  const byKey = new Map();
  for (const [key, rows] of grouped) byKey.set(key, pickPlace(rows));

  return { byId, byKey };
}
